/**
 * Route-reason classification for the model selector.
 *
 * The backend (`src/api/chat-routing.ts`) emits a human reason string with every
 * `route` SSE event — the single source of truth for *why* a given model ran the
 * turn. Most reasons are unremarkable ("user-selected", "pinned to conversation",
 * "default model"): the running model is exactly what the user would expect, so
 * surfacing them would be noise.
 *
 * A few reasons mean the running model is NOT the naive expectation — the agent
 * quietly changed course and the user deserves a heads-up:
 *   • budget-downgrade  — channel daily cap hit, dropped to a cheaper model
 *   • fallback N        — primary provider failed transiently, failed over
 *   • re-pinned         — the pinned model/provider vanished, re-routed
 *   • switched from     — the user's pick differed from the conversation pin
 *
 * This module is the one place that decides "is this route worth flagging?" Keep
 * it a pure string classifier so it is trivially testable and the UI stays dumb.
 */

export type RouteNoticeKind = 'budget' | 'fallback' | 'repin' | 'switch';

export interface RouteNotice {
  kind: RouteNoticeKind;
  /** Short badge label for the selector. */
  label: string;
}

// Ordered by severity: a budget-downgrade stacked on a fallback should read as
// the more actionable "budget" notice first. Markers match the literal phrases
// emitted in chat-routing.ts / chat.ts — keep them in sync if those change.
const NOTICE_MATCHERS: ReadonlyArray<{ kind: RouteNoticeKind; label: string; test: RegExp }> = [
  { kind: 'budget', label: 'Budget', test: /budget-downgrade/i },
  { kind: 'fallback', label: 'Failover', test: /\bfallback\s+\d+/i },
  { kind: 'repin', label: 'Re-routed', test: /re-pinned/i },
  { kind: 'switch', label: 'Switched', test: /switched from/i },
];

/**
 * Classify a backend route reason. Returns a notice when the running model
 * deviated from what the user would naively expect, or `null` for the quiet
 * common cases (so the UI shows nothing).
 */
export function classifyRouteReason(reason: string | null | undefined): RouteNotice | null {
  if (!reason) return null;
  for (const m of NOTICE_MATCHERS) {
    if (m.test.test(reason)) return { kind: m.kind, label: m.label };
  }
  return null;
}
